import React from "react";
import { Box, Chip, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getSession } from "../auth";

/**
 * ParallelSubNav — sous-navigation des pages d'une course parallèle
 * (même principe que TimingEventSubNav pour les courses individuelles).
 * Ajouté le 01.10.2026.
 *
 * Usage, en haut de chaque page :
 *   <ParallelSubNav category={category} current="brackets" />
 *
 * `current` : "athletes" | "qualifications" | "bibs" | "brackets" |
 *             "live" | "config" | "fis"
 * `visibleFor` (optionnel) : rôles pour lesquels la barre s'affiche. Ex:
 *   sur la page Live (écran public), seulement ["chronometreur"].
 *
 * Les entrées affichées dépendent du rôle, comme dans ParallelHub.jsx :
 * le rôle "colour" ne voit que Couleurs (+ Live), "paralleltimer" que
 * Bracket (+ Live), "chronometreur" tout. Sans session (spectateur sur la
 * page Live publique, écran géant…), rien n'est affiché.
 */

// ⚠️ Adresse de la page "Courses parallèles" (ParallelHub.jsx) — à adapter
// si la route est différente dans App.jsx.
const HUB_PATH = "/parallel";

const ITEMS = [
    { key: "athletes", label: "Athlètes", path: (c) => `/athletes-import/${c}`, roles: ["chronometreur"] },
    { key: "qualifications", label: "Qualifications", path: (c) => `/qualifications-import/${c}`, roles: ["chronometreur"] },
    { key: "bibs", label: "Couleurs", path: (c) => `/bibs/${c}`, roles: ["chronometreur", "colour"] },
    { key: "brackets", label: "Bracket", path: (c) => `/brackets/${c}`, roles: ["chronometreur", "paralleltimer"] },
    { key: "live", label: "🔴 Live", path: (c) => `/parallel-live/${c}`, roles: ["chronometreur", "colour", "paralleltimer"] },
    { key: "config", label: "Config impression", path: (c) => `/parallel-config/${c}`, roles: ["chronometreur"] },
    { key: "fis", label: "Export FIS", path: (c) => `/parallel-fis/${c}`, roles: ["chronometreur"] },
];

export default function ParallelSubNav({ category, current, visibleFor }) {
    const navigate = useNavigate();
    const role = getSession()?.role;
    if (!role || !category) return null;
    if (Array.isArray(visibleFor) && !visibleFor.includes(role)) return null;

    const encoded = encodeURIComponent(category);
    const items = ITEMS.filter((it) => it.roles.includes(role));

    return (
        <Box
            className="no-print"
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
                mb: 2,
                "@media print": { display: "none" },
            }}
        >
            <Chip label="← Courses parallèles" size="small" variant="outlined" clickable onClick={() => navigate(HUB_PATH)} />
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mx: 0.5 }}>
                {category}
            </Typography>
            {items.map((it) => {
                const active = it.key === current;
                return (
                    <Chip
                        key={it.key}
                        label={it.label}
                        size="small"
                        clickable={!active}
                        color={active ? "primary" : "default"}
                        variant={active ? "filled" : "outlined"}
                        onClick={active ? undefined : () => navigate(it.path(encoded))}
                    />
                );
            })}
        </Box>
    );
}